import {useCallback} from 'react';

import {
  AdCallbacksFactory,
  AdPlatformType,
  AdResponseType,
  AdType,
} from '../types';
import {once} from '../utils/once';
import {useAnalyticsLogger} from './useAnalyticsLogger';

type Props = {
  platform: AdPlatformType;
  adType: AdType;
  position?: number;
};

export const useAnalyticsCallbacks = ({
  platform,
  adType,
  position,
}: Props): AdCallbacksFactory => {
  const logger = useAnalyticsLogger({platform, adType});

  return useCallback<AdCallbacksFactory>(
    ({containerId, blockId: slotId, cleanupCallbacks}) => {
      logger.requested(slotId, position);
      const prepareShowLogging = (data: AdFox.RenderData) => {
        if (data.stickyAdNode) {
          // флоор_ад -> можно сразу отправлять показ
          logger.shown(slotId, position);
          return;
        }
        // не флоооооор_ад -> цепляем обзервера
        const element = document.getElementById(containerId);
        if (!window.IntersectionObserver || !element) {
          return;
        }
        const observer = new IntersectionObserver(
          ([entry]) => {
            if (entry.isIntersecting) {
              logger.shown(slotId, position);
              observer.disconnect();
            }
          },
          {threshold: 0.5},
        );
        observer.observe(element);
        cleanupCallbacks.push(() => observer.disconnect());
      };
      return {
        onClose: () => {
          logger.closed(slotId, position);
        },
        onError: () => {
          logger.blocked(slotId, position);
        },
        onLoad: once(() => {
          logger.gotResponse(slotId, position, {
            AdResponse: AdResponseType.HAS_ADS,
          });
        }),
        onRender: once((data) => {
          logger.rendered(slotId, position);
          prepareShowLogging(data);
        }),
        onStub: once<void>(() => {
          logger.nothingToShow(slotId, position);
          logger.gotResponse(slotId, position, {
            AdResponse: AdResponseType.NO_ADS,
          });
        }),
        onClick: () => {
          logger.clicked(slotId, position);
        },
      };
    },
    [logger, position],
  );
};
